/**
 * MeetingCard
 * Displays meeting information in a card format for lists
 */

import React from 'react';
import { Calendar, Clock, Users, Video, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Meeting } from '../../../types';
import { Avatar } from '../../ui/Avatar';
import { Badge } from '../../ui/Badge';
import styles from './MeetingCard.module.css';

export interface MeetingCardProps {
  meeting: Meeting;
  onClick?: () => void;
}

export const MeetingCard: React.FC<MeetingCardProps> = ({ meeting, onClick }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  const getBotStatusBadge = () => {
    const statusConfig = {
      invited: { variant: 'default' as const, label: 'Invited' },
      accepted: { variant: 'info' as const, label: 'Accepted' },
      joined: { variant: 'info' as const, label: 'Joined' },
      recording: { variant: 'warning' as const, label: 'Recording' },
      completed: { variant: 'success' as const, label: 'Completed' },
      failed: { variant: 'error' as const, label: 'Failed' },
    };
    const config = statusConfig[meeting.botStatus];
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getMomStatusBadge = () => {
    const statusConfig = {
      none: { variant: 'default' as const, label: 'No MoM' },
      generating: { variant: 'warning' as const, label: 'Generating' },
      draft: { variant: 'warning' as const, label: 'Draft' },
      sent: { variant: 'info' as const, label: 'Sent' },
      approved: { variant: 'success' as const, label: 'Approved' },
      jira_created: { variant: 'success' as const, label: 'JIRA Created' },
    };
    const config = statusConfig[meeting.momStatus];
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  return (
    <div className={styles.card} onClick={onClick} role="button" tabIndex={0}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h3 className={styles.title}>{meeting.title}</h3>
          <div className={styles.badges}>
            {getBotStatusBadge()}
            {meeting.momStatus !== 'none' && getMomStatusBadge()}
          </div>
        </div>
      </div>

      <div className={styles.details}>
        <div className={styles.detailItem}>
          <Calendar size={16} />
          <span>{formatDate(meeting.scheduledStart)}</span>
        </div>
        <div className={styles.detailItem}>
          <Clock size={16} />
          <span>{formatTime(meeting.scheduledStart)} - {formatTime(meeting.scheduledEnd)}</span>
        </div>
        {meeting.duration && (
          <div className={styles.detailItem}>
            <Clock size={16} />
            <span>Duration: {meeting.duration}</span>
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <div className={styles.organizer}>
          <Avatar 
            src={meeting.organizer.avatar} 
            name={meeting.organizer.name} 
            size="sm" 
          />
          <span className={styles.organizerName}>{meeting.organizer.name}</span>
        </div>
        <div className={styles.participants}>
          <Users size={16} />
          <span>{meeting.participants.length} participants</span>
        </div>
        {meeting.hasRecording && (
          <div className={styles.icon}>
            <Video size={16} />
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingCard;
